/**
 * Wires a typed React-Query tRPC instance to a TRPC HTTP client (P2-21).
 *
 * `trpc.createClient({ links: ... })` requires the typed instance, which is
 * specific to a given `AppRouter`. Pre-binding the link chain inside this
 * helper means apps never see the `as TRPCLink<TRouter>[]` cast that's
 * needed because `createPosTrpcLinks` returns the router-agnostic shape.
 */
import type { CreateTRPCClient, TRPCLink } from '@trpc/client';
import type { AnyTRPCRouter } from '@trpc/server';
import { createPosTrpcLinks, type CreatePosTrpcLinksOptions } from '../links.js';
import type { PosTrpcReact } from './create.js';

/**
 * Build a `CreateTRPCClient<TRouter>` from the typed React instance + link
 * options. Pass the result into `<PosTrpcProvider client={...}>`.
 *
 *     const trpcClient = createPosReactTrpcClient(trpc, { url: '/trpc' });
 */
type CreatesClient<TRouter extends AnyTRPCRouter> = {
  createClient: (opts: { links: TRPCLink<TRouter>[] }) => CreateTRPCClient<TRouter>;
};

export function createPosReactTrpcClient<TRouter extends AnyTRPCRouter>(
  trpc: PosTrpcReact<TRouter>,
  opts: CreatePosTrpcLinksOptions,
): CreateTRPCClient<TRouter> {
  // `trpc.createClient` lives on `CreateTRPCReactBase` but `CreateTRPCReact`
  // is a `ProtectedIntersection` whose union admits a string-literal error
  // type — so direct property access fails. The method exists at runtime;
  // cast the whole instance through `unknown` to reach it.
  const base = trpc as unknown as CreatesClient<TRouter>;
  return base.createClient({
    links: createPosTrpcLinks(opts) as TRPCLink<TRouter>[],
  });
}
